import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Form, Row, Col, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { 
  UilClock, 
  UilExclamationTriangle, 
  UilCheckCircle,
  UilCalendarAlt,
  UilEdit,
  UilTrash
} from '@iconscout/react-unicons';
import classNames from 'classnames';
import { useForm, SubmitHandler } from 'react-hook-form';
import { dateFormatter, compareDateWithToday } from 'helpers/utils';
import toast from 'react-hot-toast';

interface TaskItem {
  id: string;
  task: string;
  priority: string;
  status: string;
  due_date: string;
  is_completed: boolean;
  created_on: string;
}

interface ModernTaskListProps {
  tasks: TaskItem[];
  onCreateTask: (taskData: any) => Promise<void>;
  onUpdateTask: (taskId: string, taskData: any) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onToggleTask: (task: TaskItem) => Promise<void>;
  onSearchTask: (query: string) => void;
}

type TaskFormInputs = {
  task: string;
  priority: string;
  due_date: string;
  task_status: string;
};

const TaskPriority = ["Low", "Medium", "High"];
const TaskStatus = ["Pending", "In Progress", "Completed"];

const ModernTaskList: React.FC<ModernTaskListProps> = ({
  tasks,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onToggleTask,
  onSearchTask
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTasks, setFilteredTasks] = useState<TaskItem[]>(tasks);
  
  // Initialize filtered tasks with all tasks
  useEffect(() => {
    setFilteredTasks(tasks);
  }, [tasks]);

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    reset: resetCreate,
    formState: { errors: errorsCreate }
  } = useForm<TaskFormInputs>();

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    reset: resetEdit,
    setValue,
    formState: { errors: errorsEdit }
  } = useForm<TaskFormInputs>();

  const onSubmitCreate: SubmitHandler<TaskFormInputs> = async (data) => {
    try {
      await onCreateTask(data);
      resetCreate();
      setShowCreateModal(false);
      toast.success('Task created successfully!');
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const onSubmitEdit: SubmitHandler<TaskFormInputs> = async (data) => {
    if (!editingTask) return;
    
    try {
      await onUpdateTask(editingTask.id, data);
      resetEdit();
      setShowEditModal(false);
      setEditingTask(null);
      toast.success('Task updated successfully!');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleEditTask = (task: TaskItem) => {
    setEditingTask(task);
    setValue('task', task.task);
    setValue('priority', task.priority);
    setValue('task_status', task.status);
    setValue('due_date', task.due_date ? task.due_date.split('T')[0] : '');
    setShowEditModal(true);
  };

  const handleDeleteTask = async (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await onDeleteTask(taskId);
        toast.success('Task deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete task');
      }
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'in progress': return 'primary';
      case 'pending': return 'secondary';
      default: return 'secondary';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return UilExclamationTriangle;
      case 'medium': return UilClock;
      default: return UilCheckCircle;
    }
  };

  const getTimeAgo = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));    
    
    if (diffInHours < 24) {
      return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // No additional search handlers needed
  
  // Update filtered tasks when tasks change
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredTasks(tasks);
    } else {
      setFilteredTasks(
        tasks.filter(task => 
          task.task.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }
  }, [tasks, searchQuery]);

  return (
    <div className="modern-task-list">
      {/* Task Header */}
      <div className="task-header mb-3 mb-md-4">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-3">
          <div className="mb-2 mb-sm-0">
            <h2 className="mb-1 fw-semibold" style={{ fontSize: '1.375rem' }}>
              Tasks
              {tasks.some(task => task.is_completed) && (
                <span className="text-success fw-normal ms-2" style={{ fontSize: '1rem' }}>
                  - Completed
                </span>
              )}
            </h2>
            <small className="text-muted d-none d-sm-block">Manage your lead tasks efficiently</small>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCreateModal(true)}
            className="d-flex align-items-center gap-1"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>New Task</span>
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3">
          <div className="search-box position-relative flex-grow-1" style={{ width: '100%', maxWidth: "30rem" }}>
            <span className="position-absolute top-50 start-0 translate-middle-y ms-3" style={{ zIndex: 5 }}>
              <FontAwesomeIcon icon={faSearch} style={{ color: '#64748b' }} />
            </span>
            <Form.Control
              type="search"
              placeholder="Search tasks..."
              className="search-input search ps-5"
              value={searchQuery}
              onChange={(e) => {
                console.log({ e});
                
                const value = e.target.value;
                console.log({ value });
                
                setSearchQuery(value);
                onSearchTask(value);
              }}
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg="primary" className="fs-10">
              {filteredTasks.length} {filteredTasks.length === 1 ? 'task' : 'tasks'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Task Cards */}
      <div className="task-feed">
        {filteredTasks.length === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <UilCheckCircle size="48" className="text-muted mb-3" />
              <h5 className="text-muted">No tasks yet</h5>
              <p className="text-muted mb-4">Create your first task to get started</p>
              <Button 
                variant="primary" 
                onClick={() => setShowCreateModal(true)}
                className="d-flex align-items-center gap-2 mx-auto"
              >
                <FontAwesomeIcon icon={faPlus} />
                Create Task
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <div className="task-list">
            {filteredTasks.map((task) => {
              const PriorityIcon = getPriorityIcon(task.priority);
              const isOverdue = task.due_date && compareDateWithToday(task.due_date) === 'Due';
              const isCompleted = task.is_completed;
              
              return (
                <Card 
                  key={task.id} 
                  className={classNames(
                    'task-card mb-3 border-0',
                    {
                      'task-completed': isCompleted,
                      'task-overdue': isOverdue && !isCompleted,
                      'task-high-priority': task.priority?.toLowerCase() === 'high' && !isCompleted
                    }
                  )}
                >
                  <Card.Body className="p-2 p-sm-3">
                    <div className="d-flex align-items-start">
                      {/* Task Checkbox */}
                      <div className="task-checkbox me-2 me-sm-3 flex-shrink-0 mt-1">
                        <Form.Check.Input
                          type="checkbox"
                          checked={isCompleted}
                          onChange={() => onToggleTask(task)}
                          className="task-check-input"
                        />
                      </div>

                      {/* Task Content */}
                      <div className="task-content flex-grow-1">
                        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-2">
                          <div className="d-flex flex-wrap align-items-center gap-1 gap-sm-2 mb-2 mb-sm-0">
                            {task.priority && (
                              <Badge 
                                bg={getPriorityColor(task.priority)} 
                                className="task-priority-badge"
                              >
                                <PriorityIcon size="12" className="me-1" />
                                {task.priority}
                              </Badge>
                            )}
                            
                            {task.status && (
                              <Badge 
                                bg={getStatusColor(task.status)} 
                                className="task-status-badge"
                              >
                                {task.status}
                              </Badge>
                            )}

                            {isOverdue && !isCompleted && (
                              <Badge bg="danger">
                                Overdue
                              </Badge>
                            )}
                          </div>

                          <div className="task-actions d-flex align-items-center gap-1">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 px-1 text-muted hover-primary"
                              onClick={() => handleEditTask(task)}
                            >
                              <UilEdit size="16" />
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 px-1 text-muted hover-danger"
                              onClick={() => handleDeleteTask(task.id)}
                            >
                              <UilTrash size="16" />
                            </Button>
                          </div>
                        </div>

                        <h6 className={classNames(
                          'task-title mb-2 fs-7 lh-base',
                          {
                            'text-decoration-line-through text-muted': isCompleted
                          }
                        )}>
                          {task.task}
                        </h6>

                        <div className="task-footer d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between">
                          <div className="task-meta d-flex flex-wrap align-items-center gap-2 gap-sm-3">
                            {task.due_date && (
                              <small className={classNames(
                                'text-muted d-flex align-items-center gap-1 fs-12',
                                {
                                  'text-danger': isOverdue && !isCompleted
                                }
                              )}>
                                <UilCalendarAlt size="14" />
                                Due: {dateFormatter(task.due_date)}
                              </small>
                            )}
                            
                            <small className="text-muted d-flex align-items-center gap-1 fs-12">
                              <UilClock size="14" />
                              {getTimeAgo(task.created_on)}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Task Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" className="task-modal">
        <Modal.Header closeButton>
          <Modal.Title>Create New Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitCreate(onSubmitCreate)}>
            <Row className="g-3 task-form-row">
              <Col md="12">
                <Form.Label>
                  Task Description <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter task description..."
                  {...registerCreate("task", { required: "Task description is required" })}
                />
                {errorsCreate.task && (
                  <small className="text-danger">{errorsCreate.task.message}</small>
                )}
              </Col>
              
              <Col md="4">
                <Form.Label>Priority</Form.Label>
                <Form.Select {...registerCreate("priority")}>
                  <option value="">Select Priority</option>
                  {TaskPriority.map((item, i) => (
                    <option key={i} value={item}>{item}</option>
                  ))}
                </Form.Select>
              </Col>
              
              <Col md="4">
                <Form.Label>Status</Form.Label>
                <Form.Select {...registerCreate("task_status")}>
                  <option value="">Select Status</option>
                  {TaskStatus.map((item, i) => (
                    <option key={i} value={item}>{item}</option>
                  ))}
                </Form.Select>
              </Col>
              
              <Col md="4">
                <Form.Label>Due Date</Form.Label>
                <Form.Control type="date" {...registerCreate("due_date")} />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmitCreate(onSubmitCreate)}>
            Create Task
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Task Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" className="task-modal">
        <Modal.Header closeButton>
          <Modal.Title>Edit Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitEdit(onSubmitEdit)}>
            <Row className="g-3 task-form-row">
              <Col md="12">
                <Form.Label>
                  Task Description <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter task description..."
                  {...registerEdit("task", { required: "Task description is required" })}
                />
                {errorsEdit.task && (
                  <small className="text-danger">{errorsEdit.task.message}</small>
                )}
              </Col>
              
              <Col md="4">
                <Form.Label>Priority</Form.Label>
                <Form.Select {...registerEdit("priority")}>
                  <option value="">Select Priority</option>
                  {TaskPriority.map((item, i) => (
                    <option key={i} value={item}>{item}</option>
                  ))}
                </Form.Select>
              </Col>
              
              <Col md="4">
                <Form.Label>Status</Form.Label>
                <Form.Select {...registerEdit("task_status")}>
                  <option value="">Select Status</option>
                  {TaskStatus.map((item, i) => (
                    <option key={i} value={item}>{item}</option>
                  ))}
                </Form.Select>
              </Col>
              
              <Col md="4">
                <Form.Label>Due Date</Form.Label>
                <Form.Control type="date" {...registerEdit("due_date")} />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmitEdit(onSubmitEdit)}>
            Update Task
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ModernTaskList;